import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { IconPlus, IconTrash, IconX, IconLoader2 } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import type {
  UserColumn,
  CreateColumnInput,
  UpdateColumnInput,
  ColumnFilters,
  FilterRule,
  FilterRuleType,
  FilterOperator,
} from '@/types/column';
import {
  PRIORITY_OPTIONS,
  CATEGORY_OPTIONS,
} from '@/types/column';
import { useLabelsStore } from '@/lib/store/labels.store';

interface ColumnBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (input: CreateColumnInput | UpdateColumnInput) => Promise<void>;
  existingColumn?: UserColumn;
  matchCount?: number;
}

const RULE_TYPES: { value: FilterRuleType; label: string }[] = [
  { value: 'label', label: 'Label' },
  { value: 'sender', label: 'Sender' },
  { value: 'recipient', label: 'Recipient (Alias)' },
  { value: 'domain', label: 'Domain' },
  { value: 'keyword', label: 'Keyword' },
  { value: 'priority', label: 'Priority' },
  { value: 'category', label: 'Category' },
  { value: 'hasAttachments', label: 'Has Attachments' },
  { value: 'isUnread', label: 'Is Unread' },
  { value: 'hasThread', label: 'Has Thread' },
  { value: 'receivedWithin', label: 'Received Within' },
];

const OPERATORS: { value: FilterOperator; label: string }[] = [
  { value: 'is', label: 'is' },
  { value: 'isNot', label: 'is not' },
  { value: 'contains', label: 'contains' },
];

function getDefaultValueForType(type: FilterRuleType): string | string[] | boolean {
  switch (type) {
    case 'label':
    case 'priority':
    case 'category':
      return [];
    case 'hasAttachments':
    case 'isUnread':
    case 'hasThread':
      return true;
    case 'receivedWithin':
      return '7'; // Default to 7 days
    default:
      return '';
  }
}

function getOperatorsForType(type: FilterRuleType): FilterOperator[] {
  switch (type) {
    case 'label':
    case 'priority':
    case 'category':
      return ['is', 'isNot'];
    case 'hasAttachments':
    case 'isUnread':
    case 'hasThread':
    case 'receivedWithin':
      return ['is'];
    default:
      return ['is', 'isNot', 'contains'];
  }
}

export function ColumnBuilder({
  isOpen,
  onClose,
  onSave,
  existingColumn,
  matchCount,
}: ColumnBuilderProps) {
  const [name, setName] = useState('');
  const [logic, setLogic] = useState<'AND' | 'OR'>('AND');
  const [rules, setRules] = useState<FilterRule[]>([
    { type: 'label', operator: 'is', value: [] },
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const { labels: userLabels, isLoading: isLoadingLabels, fetchLabels: fetchLabelsFromStore } = useLabelsStore();

  // Fetch user labels when dialog opens (cached by store)
  useEffect(() => {
    if (isOpen) {
      fetchLabelsFromStore();
    }
  }, [isOpen, fetchLabelsFromStore]);

  // Reset form when opening
  useEffect(() => {
    if (isOpen) {
      if (existingColumn) {
        setName(existingColumn.name);
        setLogic(existingColumn.filters.logic);
        setRules(existingColumn.filters.rules);
      } else {
        setName('');
        setLogic('AND');
        setRules([{ type: 'label', operator: 'is', value: [] }]);
      }
    }
  }, [isOpen, existingColumn]);

  const addRule = () => {
    setRules([...rules, { type: 'label', operator: 'is', value: [] }]);
  };

  const removeRule = (index: number) => {
    if (rules.length > 1) {
      setRules(rules.filter((_, i) => i !== index));
    }
  };

  const updateRule = (index: number, updates: Partial<FilterRule>) => {
    setRules(rules.map((rule, i) => {
      if (i !== index) return rule;

      const newRule = { ...rule, ...updates };

      // Reset value and operator when type changes
      if (updates.type && updates.type !== rule.type) {
        newRule.value = getDefaultValueForType(updates.type);
        const validOperators = getOperatorsForType(updates.type);
        if (!validOperators.includes(newRule.operator)) {
          newRule.operator = validOperators[0];
        }
      }

      return newRule;
    }));
  };

  const handleSave = async () => {
    if (!name.trim() || rules.length === 0) return;

    // Validate rules have values
    const validRules = rules.filter(rule => {
      if (Array.isArray(rule.value)) {
        return rule.value.length > 0;
      }
      if (typeof rule.value === 'boolean') {
        return true;
      }
      return rule.value.trim().length > 0;
    });

    if (validRules.length === 0) return;

    setIsSaving(true);
    try {
      const filters: ColumnFilters = { logic, rules: validRules };

      await onSave({
        name,
        filters,
      });
      onClose();
    } catch (error) {
      console.error('Failed to save column:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const renderValueInput = (rule: FilterRule, index: number) => {
    switch (rule.type) {
      case 'label':
        return (
          <Select
            value={Array.isArray(rule.value) ? rule.value[0] || '' : ''}
            onValueChange={(v) => updateRule(index, { value: v ? [v] : [] })}
            disabled={isLoadingLabels}
          >
            <SelectTrigger className="h-7 text-xs">
              {isLoadingLabels ? (
                <IconLoader2 className="h-3 w-3 animate-spin" />
              ) : (
                <SelectValue placeholder="Select label" />
              )}
            </SelectTrigger>
            <SelectContent>
              {userLabels.map(label => (
                <SelectItem key={label.id} value={label.name} className="text-xs">
                  <span className="flex items-center gap-1.5">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: label.color }}
                    />
                    {label.name}
                  </span>
                </SelectItem>
              ))}
              {userLabels.length === 0 && !isLoadingLabels && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  No labels found
                </div>
              )}
            </SelectContent>
          </Select>
        );

      case 'priority':
        return (
          <Select
            value={Array.isArray(rule.value) ? rule.value[0] || '' : ''}
            onValueChange={(v) => updateRule(index, { value: v ? [v] : [] })}
          >
            <SelectTrigger className="h-7 text-xs">
              <SelectValue placeholder="Select priority" />
            </SelectTrigger>
            <SelectContent>
              {PRIORITY_OPTIONS.map(p => (
                <SelectItem key={p} value={p} className="text-xs capitalize">
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'category':
        return (
          <Select
            value={Array.isArray(rule.value) ? rule.value[0] || '' : ''}
            onValueChange={(v) => updateRule(index, { value: v ? [v] : [] })}
          >
            <SelectTrigger className="h-7 text-xs">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map(c => (
                <SelectItem key={c} value={c} className="text-xs">
                  {c === 'do_nothing' ? 'Inbox' : c.charAt(0).toUpperCase() + c.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'hasAttachments':
      case 'isUnread':
      case 'hasThread':
        return (
          <Select
            value={rule.value === true ? 'true' : 'false'}
            onValueChange={(v) => updateRule(index, { value: v === 'true' })}
          >
            <SelectTrigger className="h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true" className="text-xs">Yes</SelectItem>
              <SelectItem value="false" className="text-xs">No</SelectItem>
            </SelectContent>
          </Select>
        );

      case 'receivedWithin':
        return (
          <Select
            value={typeof rule.value === 'string' ? rule.value : '7'}
            onValueChange={(v) => updateRule(index, { value: v })}
          >
            <SelectTrigger className="h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1" className="text-xs">Today</SelectItem>
              <SelectItem value="3" className="text-xs">3 days</SelectItem>
              <SelectItem value="7" className="text-xs">7 days</SelectItem>
              <SelectItem value="14" className="text-xs">14 days</SelectItem>
              <SelectItem value="30" className="text-xs">30 days</SelectItem>
            </SelectContent>
          </Select>
        );

      default:
        return (
          <Input
            className="h-7 text-xs"
            placeholder={
              rule.type === 'domain' ? 'example.com' :
              rule.type === 'recipient' ? 'jobs@boxento.com' :
              'Enter value...'
            }
            value={typeof rule.value === 'string' ? rule.value : ''}
            onChange={(e) => updateRule(index, { value: e.target.value })}
          />
        );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-base">
            {existingColumn ? 'Edit Column' : 'Create Custom Column'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-1">
            <Label className="text-xs">Column Name</Label>
            <Input
              className="h-8 text-sm"
              placeholder="e.g., Travel & Bookings"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Filter Logic */}
          <div className="space-y-1">
            <Label className="text-xs">Match emails where</Label>
            <div className="flex items-center gap-2">
              <Button
                variant={logic === 'AND' ? 'default' : 'outline'}
                size="sm"
                className="text-xs h-7"
                onClick={() => setLogic('AND')}
              >
                All rules match
              </Button>
              <Button
                variant={logic === 'OR' ? 'default' : 'outline'}
                size="sm"
                className="text-xs h-7"
                onClick={() => setLogic('OR')}
              >
                Any rule matches
              </Button>
            </div>
          </div>

          {/* Rules */}
          <div className="space-y-2">
            <Label className="text-xs">Filter Rules</Label>
            <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
              {rules.map((rule, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-muted/50 rounded-md">
                  <Select
                    value={rule.type}
                    onValueChange={(v) => updateRule(index, { type: v as FilterRuleType })}
                  >
                    <SelectTrigger className="h-7 text-xs w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RULE_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value} className="text-xs">
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={rule.operator}
                    onValueChange={(v) => updateRule(index, { operator: v as FilterOperator })}
                  >
                    <SelectTrigger className="h-7 text-xs w-[80px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getOperatorsForType(rule.type).map(op => (
                        <SelectItem key={op} value={op} className="text-xs">
                          {OPERATORS.find(o => o.value === op)?.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="flex-1">
                    {renderValueInput(rule, index)}
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => removeRule(index)}
                    disabled={rules.length === 1}
                  >
                    <IconTrash className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                </div>
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              className="text-xs h-7 w-full"
              onClick={addRule}
            >
              <IconPlus className="h-3 w-3 mr-1" />
              Add Rule
            </Button>
          </div>

          {/* Match Preview */}
          {matchCount !== undefined && (
            <div className="text-xs text-muted-foreground bg-muted/30 px-3 py-2 rounded">
              {matchCount} email{matchCount !== 1 ? 's' : ''} match{matchCount === 1 ? 'es' : ''} this filter
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
          >
            {isSaving ? 'Saving...' : existingColumn ? 'Save Changes' : 'Create Column'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
